import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import { DriverViewModel } from "@app/shared/models/drivers/DriverViewModel.model";
import { ChartConfiguration, ChartData, ChartType } from "chart.js";
import { ConfirmationService } from "primeng/api";
import { DriverService } from "@app/driver/shared/driver.service";
import {
  DriverEventViewModel,
  DriverEventType,
  DriverEventTypeHelper,
  DriverEventChangeViewModel,
  DriverEventChangeReason,
  DriverEventChangeReasonHelper,
} from "@app/shared/models/drivers/DriverEventViewModel.model";
import { ToastrService } from "ngx-toastr";
import * as XLSX from "xlsx";

type DutyStatusLabel = "Off" | "Sleeper" | "Driving" | "On" | "Unknown";

interface DutyStatusRecord {
  status: DutyStatusLabel;
  start: Date;
  end: Date;
}

interface DutyStatusIntervalResponse {
  did: string | null;
  dutyStatus: number;
  effectiveUtc: string;
  createdUtc: string;
  startUtc: string;
  endUtc: string;
  packetId: number;
}

interface DateColorMap {
  [dateKey: string]: string;
}

interface EventTypeOption {
  label: string;
  value: string;
  disabled?: boolean;
}

interface TimeOffRecord {
  id: number | string;
  startDate: Date;
  endDate: Date;
  eventType: string;
  notes: string;
  changeCount?: number;
  dateCreated?: Date;
  uploadBatchId?: string;
}

interface ScheduleUploadDateRange {
  start: Date;
  end: Date;
}

interface ScheduleUploadConflict {
  existingEventId: string;
  existingStart: Date;
  existingEnd: Date;
  existingEventType: string;
  overlapStart: Date;
  overlapEnd: Date;
}

interface ScheduleUploadPreview {
  driverId: string;
  driverName: string;
  spreadsheetDriverName?: string;
  driverNameMismatch: boolean;
  spreadsheetYear?: number;
  dateRanges: ScheduleUploadDateRange[];
  conflicts: ScheduleUploadConflict[];
  totalDaysOff: number;
}

interface PreviewCalendarMonth {
  year: number;
  month: number;
  label: string;
  weeks: (Date | null)[][];
}

@Component({
  selector: "app-driver-schedule",
  templateUrl: "./driver-schedule.component.html",
  styleUrls: ["./driver-schedule.component.css"],
})
export class DriverScheduleComponent implements OnInit {
  @Input() driver: DriverViewModel;
  @Input() isEditMode: boolean;

  @Output() hasChanges = new EventEmitter<boolean>();

  constructor(
    private confirmationService: ConfirmationService,
    private driverService: DriverService,
    private toastr: ToastrService
  ) {}

  // Calendar properties
  selectedDate: Date = new Date();
  selectedHoursDate: Date = new Date();
  private hoursDateChangeTimeout: any = null;
  dateColorMap: DateColorMap = {};
  private dateEventTypeMap: { [dateKey: string]: string[] } = {};
  private firstDayBackMap: { [dateKey: string]: boolean } = {};
  private adjustedDayMap: { [dateKey: string]: boolean } = {};
  private projected34HourRestartMap: { [dateKey: string]: boolean } = {};
  private readonly lockedCalendarDate: Date = new Date();

  // Dialog properties
  displayDialog: boolean = false;
  newTimeOffRecord: TimeOffRecord = this.getEmptyTimeOffRecord();
  isEditingRecord: boolean = false;
  displayChangeDialog: boolean = false;
  changeReason: DriverEventChangeReason = DriverEventChangeReason.DriverRequested;
  changeReasonNote: string = "";
  originalStartDate: Date = new Date();
  originalEndDate: Date = new Date();
  pendingUpdate: TimeOffRecord | null = null;
  eventChanges: DriverEventChangeViewModel[] = [];
  displayChangesHistoryDialog: boolean = false;

  // Schedule Upload properties
  displayUploadDialog: boolean = false;
  uploadPreview: ScheduleUploadPreview | null = null;
  isUploadParsing: boolean = false;
  isUploadConfirming: boolean = false;
  lastUploadBatchId: string | null = null;
  showUndoBanner: boolean = false;

  // Event Type options for dropdown
  private readonly baseEventTypeOptions: EventTypeOption[] = [
    { label: "Sick", value: "Sick" },
    { label: "Home Time", value: "Home Time" },
    { label: "PTO", value: "PTO" },
    { label: "Time Off", value: "Time Off" },
  ];

  // Time Off records
  public timeOffRecords: TimeOffRecord[] = [];
  private isLoadingEvents: boolean = false;

  private readonly dutyStatusCategories: DutyStatusLabel[] = ["Off", "Sleeper", "Driving", "On"];
  private readonly dutyStatusColors: Record<DutyStatusLabel, string> = {
    Off: "rgba(135, 206, 250, 0.8)",
    Sleeper: "rgba(76, 175, 80, 0.8)",
    Driving: "rgba(158, 158, 158, 0.8)",
    On: "rgba(244, 67, 54, 0.8)",
    Unknown: "rgba(96, 125, 139, 0.8)",
  };

  private dutyStatusRecords: DutyStatusRecord[] = [];
  private selectedDateRecordsCache: DutyStatusRecord[] = [];
  private isLoadingDutyStatuses: boolean = false;
  public isLoadingChartData: boolean = false;
  private get dutyStatusCategoryDisplayLabels(): string[] {
    return this.dutyStatusCategories.map((status) => status.toUpperCase());
  }

  public barChartType: ChartType = "bar";
  public barChartData: ChartData<"bar"> = {
    labels: ["Off", "Sleeper", "Driving", "On"],
    datasets: [],
  };
  public barChartOptions: ChartConfiguration["options"];

  ngOnInit() {
    this.initializeChartOptions();
    this.loadDriverEvents();
    this.loadDutyStatuses();
  }

  public get ptoOverrideValue(): boolean {
    if (!this.driver) {
      return false;
    }

    return this.driver.isPtoEligible !== null && this.driver.isPtoEligible !== undefined;
  }

  public set ptoOverrideValue(value: boolean) {
    if (!this.driver) {
      return;
    }

    if (value) {
      if (this.driver.isPtoEligible === null || this.driver.isPtoEligible === undefined) {
        this.driver.isPtoEligible = this.driver.isPtoEligibleDefault ?? false;
      }
    } else {
      if (this.driver.isPtoEligible !== null) {
        this.driver.isPtoEligible = null;
      }
    }

    this.onFieldChange();
  }

  public get ptoEligibleValue(): boolean {
    if (!this.driver) {
      return false;
    }

    if (this.driver.isPtoEligible !== null && this.driver.isPtoEligible !== undefined) {
      return this.driver.isPtoEligible;
    }

    return this.driver.isPtoEligibleDefault ?? false;
  }

  public set ptoEligibleValue(value: boolean) {
    if (!this.driver) {
      return;
    }

    if (this.driver.isPtoEligible !== value) {
      this.driver.isPtoEligible = value;
      this.onFieldChange();
    }
  }

  public get ptoEligibilityStatusText(): string {
    if (!this.driver) {
      return "Driver PTO Eligibility: Unknown";
    }

    const eligibility = this.ptoEligibleValue ? "Driver PTO Eligible" : "Driver Not PTO Eligible";
    const source = this.ptoOverrideValue ? "Override" : "Default";

    return `${eligibility} (${source})`;
  }

  public get eventTypeOptions(): EventTypeOption[] {
    const isEligibleForPto = this.ptoEligibleValue;

    return this.baseEventTypeOptions.map((option) => ({
      ...option,
      disabled: option.value === "PTO" && !isEligibleForPto,
    }));
  }

  private loadDriverEvents(): void {
    if (!this.driver || !this.driver.id || this.isLoadingEvents) {
      return;
    }

    this.isLoadingEvents = true;
    this.driverService.getDriverEvents(String(this.driver.id)).subscribe({
      next: (events: any) => {
        this.timeOffRecords = events.map((event: DriverEventViewModel) =>
          this.mapEventToTimeOffRecord(event)
        );
        this.sortTimeOffRecords();
        this.buildDateColorMap();
        this.buildProjected34HourRestarts();
        this.isLoadingEvents = false;
      },
      error: (error) => {
        console.error("Error loading driver events:", error);
        this.toastr.error("Failed to load driver events", "Error");
        this.isLoadingEvents = false;
      },
    });
  }

  private sortTimeOffRecords(): void {
    this.timeOffRecords.sort((a, b) => {
      const dateA = new Date(a.startDate).getTime();
      const dateB = new Date(b.startDate).getTime();
      return dateB - dateA; // Sort by start date descending (newest first)
    });
  }

  private loadDutyStatuses(): void {
    if (!this.driver || !this.driver.id || this.isLoadingDutyStatuses) {
      return;
    }

    this.isLoadingDutyStatuses = true;
    this.driverService.getRecentDutyStatuses(String(this.driver.id)).subscribe({
      next: (intervals: DutyStatusIntervalResponse[]) => {
        this.dutyStatusRecords = this.mapIntervalsToRecords(intervals);
        // Initialize cache with today's records for the chart
        this.selectedDateRecordsCache = this.getRecordsForDate(this.selectedHoursDate);
        this.refreshDutyStatusVisuals();
        this.isLoadingDutyStatuses = false;
      },
      error: (error) => {
        console.error("Error loading duty status intervals:", error);
        this.toastr.error("Failed to load duty status history", "Error");
        this.dutyStatusRecords = [];
        this.selectedDateRecordsCache = [];
        this.refreshDutyStatusVisuals();
        this.isLoadingDutyStatuses = false;
      },
    });
  }

  private mapIntervalsToRecords(intervals: DutyStatusIntervalResponse[]): DutyStatusRecord[] {
    return intervals
      .map((interval) => {
        const start = new Date(interval.startUtc);
        const end = new Date(interval.endUtc);

        if (isNaN(start.getTime()) || isNaN(end.getTime()) || end.getTime() <= start.getTime()) {
          return null;
        }

        return {
          status: this.mapDutyStatusToLabel(interval.dutyStatus),
          start,
          end,
        } as DutyStatusRecord;
      })
      .filter((record): record is DutyStatusRecord => record !== null);
  }

  private mapDutyStatusToLabel(value: number): DutyStatusLabel {
    switch (value) {
      case 1:
        return "Driving";
      case 2:
        return "On";
      case 3:
        return "Off";
      case 4:
        return "Sleeper";
      default:
        return "Unknown";
    }
  }

  private refreshDutyStatusVisuals(): void {
    this.updateChartData();
  }

  public getDutyStatusColor(status: DutyStatusLabel): string {
    return this.dutyStatusColors[status] ?? this.dutyStatusColors.Unknown;
  }

  private getRecordsForDate(date: Date): DutyStatusRecord[] {
    return this.dutyStatusRecords.filter((record) => this.isSameDay(record.start, date));
  }

  private isSameDay(dateA: Date, dateB: Date): boolean {
    return (
      dateA.getFullYear() === dateB.getFullYear() &&
      dateA.getMonth() === dateB.getMonth() &&
      dateA.getDate() === dateB.getDate()
    );
  }

  /**
   * Based on the most recent HosReset34h event, project the next restart ~7 days later
   * and mark that calendar date in projected34HourRestartMap.
   */
  private buildProjected34HourRestarts(): void {
    this.projected34HourRestartMap = {};

    const resets = this.timeOffRecords
      .filter((r) => r.eventType === "34h Reset")
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

    if (resets.length === 0) return;

    const last = { start: new Date(resets[resets.length - 1].startDate) };

    // Projected start: 7 days after the last restart's start
    const projectedStart = new Date(last.start);
    projectedStart.setDate(projectedStart.getDate() + 7);
    projectedStart.setHours(0, 0, 0, 0);

    // Only show if it hasn't already passed (allow same-day)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (projectedStart < today) return;

    this.projected34HourRestartMap[this.getDateKey(projectedStart)] = true;
  }

  private buildDateColorMap(): void {
    // Clear existing maps
    this.dateColorMap = {};
    this.dateEventTypeMap = {};
    this.firstDayBackMap = {};
    this.adjustedDayMap = {};

    // Map to store colors for time off events
    const eventTypeColors: { [key: string]: string } = {
      Sick: "#F44336", // Red
      "Home Time": "#4CAF50", // Green
      PTO: "#FFC107", // Yellow/Orange
      "Time Off": "#2196F3", // Blue
    };

    // Process each time off record
    this.timeOffRecords.forEach((record) => {
      const startDate = new Date(record.startDate);
      const endDate = new Date(record.endDate);

      // Mark all dates in the range
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateKey = this.getDateKey(currentDate);
        // Template events get purple regardless of type string
        this.dateColorMap[dateKey] = record.uploadBatchId
          ? "#9C27B0"
          : eventTypeColors[record.eventType] || "#2196F3";
        const typesForDate = this.dateEventTypeMap[dateKey] ?? [];
        if (!typesForDate.includes(record.eventType)) {
          typesForDate.push(record.eventType);
        }
        this.dateEventTypeMap[dateKey] = typesForDate;

        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Mark the first day back (day after end date)
      const firstDayBack = new Date(endDate);
      firstDayBack.setDate(firstDayBack.getDate() + 1);
      const firstDayBackKey = this.getDateKey(firstDayBack);
      // Only mark as first day back if it's not already a time off day
      if (!this.dateColorMap[firstDayBackKey]) {
        this.firstDayBackMap[firstDayBackKey] = true;
      }
    });
  }

  private mapEventToTimeOffRecord(event: DriverEventViewModel): TimeOffRecord {
    return {
      id: event.id as any,
      startDate: this.parseLocalDate(event.start),
      endDate: this.parseLocalDate(event.end),
      eventType: DriverEventTypeHelper.toString(event.eventType),
      notes: event.notes || "",
      changeCount: event.changeCount || 0,
      dateCreated: event.dateCreatedUtc ? new Date(event.dateCreatedUtc) : undefined,
      uploadBatchId: event.uploadBatchId || undefined,
    };
  }

  public get changeReasonOptions() {
    return DriverEventChangeReasonHelper.getOptions();
  }

  private mapTimeOffRecordToEvent(record: TimeOffRecord): DriverEventViewModel {
    // Convert Date to DateOnly format (YYYY-MM-DD)
    const formatDateOnly = (date: Date): Date => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      return new Date(year, month, day);
    };

    return {
      id: String(record.id),
      start: formatDateOnly(record.startDate),
      end: formatDateOnly(record.endDate),
      eventType: DriverEventTypeHelper.fromString(record.eventType),
      notes: record.notes,
      driverId: String(this.driver.id),
    };
  }

  private getDateKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  }

  public getDateStyle(dateObj: any): any {
    // PrimeNG passes a date object with day, month, year properties
    const date = new Date(dateObj.year, dateObj.month, dateObj.day);
    const dateKey = this.getDateKey(date);
    const color = this.dateColorMap[dateKey];

    if (color) {
      return {
        "background-color": color,
        color: "#ffffff",
        "border-radius": "50%",
        "font-weight": "bold",
      };
    }

    // Check for first day back
    if (this.firstDayBackMap[dateKey]) {
      return {
        "background-color": "#4CAF50",
        color: "#ffffff",
        "border-radius": "50%",
        "font-weight": "bold",
      };
    }

    // Check for projected 34-hour restart (estimate)
    if (this.projected34HourRestartMap[dateKey]) {
      return {}; // Visual styling handled by CSS class
    }

    return {};
  }

  public isProjected34HourRestart(dateObj: any): boolean {
    const date = new Date(dateObj.year, dateObj.month, dateObj.day);
    return !!this.projected34HourRestartMap[this.getDateKey(date)];
  }

  public isAdjustedDay(dateObj: any): boolean {
    const date = new Date(dateObj.year, dateObj.month, dateObj.day);
    return !!this.adjustedDayMap[this.getDateKey(date)];
  }

  public hasEventsOnDate(dateObj: any): boolean {
    // PrimeNG passes a date object with day, month, year properties
    const date = new Date(dateObj.year, dateObj.month, dateObj.day);
    const dateKey = this.getDateKey(date);
    return !!this.dateColorMap[dateKey] || !!this.projected34HourRestartMap[dateKey];
  }

  public isFirstDayBack(dateObj: any): boolean {
    const date = new Date(dateObj.year, dateObj.month, dateObj.day);
    return !!this.firstDayBackMap[this.getDateKey(date)];
  }

  public getDateTooltip(dateObj: any): string | null {
    const date = new Date(dateObj.year, dateObj.month, dateObj.day);
    const dateKey = this.getDateKey(date);

    // Check for first day back
    if (this.firstDayBackMap[dateKey]) {
      return "First Day Back";
    }

    // Check for projected 34-hour restart
    if (this.projected34HourRestartMap[dateKey]) {
      return "Projected 34-hr Restart (Estimate)";
    }

    const eventTypes = this.dateEventTypeMap[dateKey];
    if (!eventTypes || eventTypes.length === 0) {
      return null;
    }

    return eventTypes.join(", ");
  }

  private initializeChartOptions(): void {
    this.barChartOptions = {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 400, // Faster animation
        easing: "easeOutQuart",
      },
      layout: {
        autoPadding: true,
      },
      plugins: {
        legend: {
          display: false, // Hide legend since we have the duration summary panel
        },
        title: {
          display: false,
          text: "Driver Duty Status - 24 Hour Timeline",
          font: {
            size: 16,
            weight: "bold",
          },
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const data = context.raw as [number, number];
              const start = this.formatHourValue(data[0]);
              const end = this.formatHourValue(data[1]);
              const duration = this.formatDurationFromHours(data[1] - data[0]);
              return `${context.dataset.label}: ${start} - ${end} (${duration})`;
            },
          },
        },
      },
      scales: {
        x: {
          type: "linear",
          min: 0,
          max: 24,
          stacked: false, // Explicitly disable x-axis stacking to use absolute positions
          ticks: {
            stepSize: 1,
            callback: (value: any) => {
              return value.toString().padStart(2, "0");
            },
          },
          title: {
            display: false,
            text: "Time (24-hour format)",
            font: {
              size: 12,
            },
          },
          grid: {
            display: true,
            color: "rgba(0, 0, 0, 0.1)",
          },
        },
        y: {
          type: "category",
          labels: this.dutyStatusCategoryDisplayLabels,
          stacked: true, // Enable stacking to keep bars on the same line
          grid: {
            display: true,
          },
        },
      },
    };
  }

  private updateChartData(): void {
    const datasets: any[] = [];
    const selectedDateRecords = this.selectedDateRecordsCache.sort(
      (a, b) => a.start.getTime() - b.start.getTime()
    );

    selectedDateRecords.forEach((record) => {
      if (!this.dutyStatusCategories.includes(record.status)) {
        return;
      }

      const categoryIndex = this.dutyStatusCategories.indexOf(record.status);
      const startHour = this.getHourFraction(record.start);
      let endHour = this.getHourFraction(record.end);

      // If the end date is on a different day than the start date, cap endHour at 24
      if (!this.isSameDay(record.start, record.end)) {
        endHour = 24;
      }

      // Skip records where start and end are the same (would be invisible anyway)
      if (startHour === endHour) {
        return;
      }

      const categoryData = this.dutyStatusCategories.map((_, index) => {
        if (index === categoryIndex) {
          return [startHour, endHour] as [number, number];
        }
        return null;
      });

      datasets.push({
        label: record.status.toUpperCase(),
        data: categoryData,
        backgroundColor: this.dutyStatusColors[record.status],
        borderColor: this.dutyStatusColors[record.status],
        borderWidth: 1,
        borderSkipped: false,
        barPercentage: 1,
        categoryPercentage: 1,
      });
    });

    this.barChartData = {
      labels: [...this.dutyStatusCategoryDisplayLabels],
      datasets,
    };
  }

  private getHourFraction(date: Date): number {
    return date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600;
  }

  private formatHourValue(hour: number): string {
    const h = Math.floor(hour);
    const m = Math.floor((hour - h) * 60);
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  }

  public formatTime(date: Date): string {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return "--:--";
    }

    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  }

  private formatDurationFromHours(hours: number): string {
    const totalSeconds = Math.max(0, Math.round(hours * 3600));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  }

  public getTotalDuration(status: DutyStatusLabel): string {
    const records = this.selectedDateRecordsCache.filter((record) => record.status === status);
    const totalHours = records.reduce(
      (sum, record) => sum + (record.end.getTime() - record.start.getTime()) / 3600000,
      0
    );
    return this.formatDurationFromHours(totalHours);
  }

  public onHoursDateChange(): void {
    // Clear any existing timeout
    if (this.hoursDateChangeTimeout) {
      clearTimeout(this.hoursDateChangeTimeout);
    }

    // Debounce the date change to avoid interrupting typing
    this.hoursDateChangeTimeout = setTimeout(() => {
      this.loadDutyStatusesForDate(this.selectedHoursDate);
    }, 500);
  }

  public onHoursDateSelect(): void {
    // Clear any pending timeout
    if (this.hoursDateChangeTimeout) {
      clearTimeout(this.hoursDateChangeTimeout);
    }

    // Load immediately when selecting from calendar
    this.loadDutyStatusesForDate(this.selectedHoursDate);
  }

  private loadDutyStatusesForDate(date: Date): void {
    if (!this.driver || !this.driver.id || this.isLoadingChartData) {
      return;
    }

    this.isLoadingChartData = true;
    try {
      this.driverService.getDutyStatusesForDate(String(this.driver.id), date).subscribe({
        next: (intervals: DutyStatusIntervalResponse[]) => {
          this.selectedDateRecordsCache = this.mapIntervalsToRecords(intervals);
          this.updateChartData();
          this.isLoadingChartData = false;
        },
        error: (error) => {
          console.error("Error loading duty status for date:", error);
          this.toastr.error("Failed to load duty status for selected date", "Error");
          this.selectedDateRecordsCache = [];
          this.updateChartData();
          this.isLoadingChartData = false;
        },
      });
    } catch {
      this.isLoadingChartData = false;
    }
  }

  public getGroupedRecordsByDate(): { date: Date; records: DutyStatusRecord[] }[] {
    const grouped = new Map<string, { date: Date; records: DutyStatusRecord[] }>();

    this.dutyStatusRecords.forEach((record) => {
      const dateKey = record.start.toDateString();
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, { date: new Date(record.start), records: [] });
      }

      grouped.get(dateKey)!.records.push(record);
    });

    return Array.from(grouped.values())
      .sort((a, b) => b.date.getTime() - a.date.getTime())
      .map((group) => ({
        date: group.date,
        records: [...group.records].sort((a, b) => b.start.getTime() - a.start.getTime()),
      }));
  }

  public formatDate(date: Date): string {
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  }

  onFieldChange() {
    this.hasChanges.emit(true);
  }

  private getEmptyTimeOffRecord(): TimeOffRecord {
    const startDate = new Date(this.selectedDate);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(startDate);

    return {
      id: "" as any,
      startDate: startDate,
      endDate: endDate,
      eventType: "",
      notes: "",
    };
  }

  public showAddDialog(): void {
    this.isEditingRecord = false;
    this.newTimeOffRecord = this.getEmptyTimeOffRecord();
    this.eventChanges = [];
    this.displayDialog = true;
  }

  public editTimeOffRecord(record: TimeOffRecord): void {
    this.isEditingRecord = true;
    this.newTimeOffRecord = { ...record };
    this.displayDialog = true;

    // Load change history if editing an existing record
    if (record.id) {
      this.loadEventChanges(record.id);
    } else {
      this.eventChanges = [];
    }
  }

  private loadEventChanges(eventId: number | string): void {
    this.driverService.getEventChanges(String(eventId)).subscribe({
      next: (changes: DriverEventChangeViewModel[]) => {
        this.eventChanges = changes;
      },
      error: (error) => {
        console.error("Error loading event changes:", error);
        this.eventChanges = [];
      },
    });
  }

  public areDatesEqual(date1: Date, date2: Date): boolean {
    if (!date1 || !date2) return false;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }

  public getOriginalChange(): DriverEventChangeViewModel | null {
    if (this.eventChanges.length === 0) return null;
    // Return the oldest change (last in the list if sorted by date descending)
    return this.eventChanges[this.eventChanges.length - 1];
  }

  public deleteTimeOffRecord(record: TimeOffRecord): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete this ${record.eventType} event from ${this.formatDate(record.startDate)}?`,
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        this.driverService.deleteDriverEvent(String(record.id)).subscribe({
          next: () => {
            const index = this.timeOffRecords.findIndex((r) => r.id === record.id);
            if (index > -1) {
              this.timeOffRecords.splice(index, 1);
              this.buildDateColorMap(); // Update calendar colors
              this.toastr.success("Event deleted successfully", "Success");
              this.hasChanges.emit(true);
            }
          },
          error: (error) => {
            console.error("Error deleting event:", error);
            this.toastr.error("Failed to delete event", "Error");
          },
        });
      },
    });
  }

  public saveTimeOffRecord(): void {
    // Validate required fields
    if (!this.newTimeOffRecord.eventType || this.newTimeOffRecord.eventType.trim() === "") {
      this.toastr.warning("Event Type is required", "Validation Error");
      return;
    }

    if (this.newTimeOffRecord.eventType === "PTO" && !this.ptoEligibleValue) {
      this.toastr.warning("Driver is not PTO eligible", "Validation Error");
      return;
    }

    if (!this.newTimeOffRecord.notes || this.newTimeOffRecord.notes.trim() === "") {
      this.toastr.warning("Notes are required", "Validation Error");
      return;
    }

    // Validate date ranges
    if (this.newTimeOffRecord.endDate < this.newTimeOffRecord.startDate) {
      this.toastr.warning("End Date cannot be before Start Date", "Validation Error");
      return;
    }

    if (this.isEditingRecord) {
      // Check if dates have changed
      const originalRecord = this.timeOffRecords.find((r) => r.id === this.newTimeOffRecord.id);
      if (originalRecord) {
        const startChanged =
          originalRecord.startDate.getTime() !== this.newTimeOffRecord.startDate.getTime();
        const endChanged =
          originalRecord.endDate.getTime() !== this.newTimeOffRecord.endDate.getTime();

        if (startChanged || endChanged) {
          // Store original dates and new record for later
          this.originalStartDate = originalRecord.startDate;
          this.originalEndDate = originalRecord.endDate;
          this.pendingUpdate = { ...this.newTimeOffRecord };

          // Show change reason dialog
          this.displayChangeDialog = true;
          return;
        }
      }
    }

    // No date changes, proceed with save
    this.performSaveTimeOffRecord();
  }

  public confirmChangeReason(): void {
    if (!this.pendingUpdate) return;

    // Save the change reason first
    const change: DriverEventChangeViewModel = {
      id: "",
      driverEventId: String(this.pendingUpdate.id),
      previousStart: this.originalStartDate,
      previousEnd: this.originalEndDate,
      newStart: this.pendingUpdate.startDate,
      newEnd: this.pendingUpdate.endDate,
      reason: this.changeReason,
      reasonNote: this.changeReasonNote || "",
      dateCreatedUtc: new Date(),
      changedByUserId: "",
    };

    this.driverService.createEventChange(change).subscribe({
      next: () => {
        // Now update the event
        this.newTimeOffRecord = this.pendingUpdate!;
        this.performSaveTimeOffRecord();
        this.displayChangeDialog = false;
        this.changeReasonNote = "";
        this.changeReason = DriverEventChangeReason.DriverRequested;
        this.pendingUpdate = null;
      },
      error: (error) => {
        console.error("Error saving change reason:", error);
        this.toastr.error("Failed to save change reason", "Error");
      },
    });
  }

  public cancelChangeReason(): void {
    this.displayChangeDialog = false;
    this.changeReasonNote = "";
    this.changeReason = DriverEventChangeReason.DriverRequested;
    this.pendingUpdate = null;
  }

  private performSaveTimeOffRecord(): void {
    const eventData = this.mapTimeOffRecordToEvent(this.newTimeOffRecord);

    if (this.isEditingRecord) {
      // Update existing record
      this.driverService.updateDriverEvent(eventData).subscribe({
        next: (updatedEvent: any) => {
          const index = this.timeOffRecords.findIndex((r) => r.id === this.newTimeOffRecord.id);
          if (index > -1) {
            this.timeOffRecords[index] = this.mapEventToTimeOffRecord(updatedEvent);
          }
          this.sortTimeOffRecords();
          this.buildDateColorMap();
          this.toastr.success("Event updated successfully", "Success");
          this.hasChanges.emit(true);
          this.closeDialog();
        },
        error: (error) => {
          console.error("Error updating event:", error);
          this.toastr.error("Failed to update event", "Error");
        },
      });
    } else {
      // Create new record
      this.driverService.createDriverEvent(eventData).subscribe({
        next: (newEvent: any) => {
          this.timeOffRecords.push(this.mapEventToTimeOffRecord(newEvent));
          this.sortTimeOffRecords();
          this.buildDateColorMap();
          this.toastr.success("Event created successfully", "Success");
          this.hasChanges.emit(true);
          this.closeDialog();
        },
        error: (error) => {
          console.error("Error creating event:", error);
          this.toastr.error("Failed to create event", "Error");
        },
      });
    }
  }

  public closeDialog(): void {
    this.displayDialog = false;
    this.newTimeOffRecord = this.getEmptyTimeOffRecord();
    this.eventChanges = [];
  }

  public viewChangeHistory(record: TimeOffRecord): void {
    this.driverService.getEventChanges(String(record.id)).subscribe({
      next: (changes: any) => {
        this.eventChanges = changes.map((c: any) => ({
          ...c,
          previousStart: new Date(c.previousStart),
          previousEnd: new Date(c.previousEnd),
          newStart: new Date(c.newStart),
          newEnd: new Date(c.newEnd),
          dateCreatedUtc: new Date(c.dateCreatedUtc),
        }));
        this.displayChangesHistoryDialog = true;
      },
      error: (error) => {
        console.error("Error loading change history:", error);
        this.toastr.error("Failed to load change history", "Error");
      },
    });
  }

  public closeChangesHistoryDialog(): void {
    this.displayChangesHistoryDialog = false;
    this.eventChanges = [];
  }

  public getChangeReasonLabel(reason: DriverEventChangeReason): string {
    return DriverEventChangeReasonHelper.toString(reason);
  }

  public onDialogShow(): void {
    // Remove focus from any auto-focused input when dialog opens
    setTimeout(() => {
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement && activeElement.tagName === "INPUT") {
        activeElement.blur();
      }
    }, 0);
  }

  public onCalendarDateClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  public onCalendarSelect(event: any): void {
    if (event?.originalEvent) {
      event.originalEvent.preventDefault();
      event.originalEvent.stopPropagation();
    }
    this.selectedDate = new Date(this.lockedCalendarDate);
  }

  private serializeDriver(driver: DriverViewModel | undefined): string | null {
    if (!driver) {
      return null;
    }

    try {
      return JSON.stringify(driver);
    } catch {
      return null;
    }
  }

  // Schedule Upload Methods

  public isTemplateEvent(record: TimeOffRecord): boolean {
    return !!record.uploadBatchId;
  }

  public showUploadDialog(): void {
    this.displayUploadDialog = true;
    this.uploadPreview = null;
    this.isUploadParsing = false;
    this.isUploadConfirming = false;
  }

  public closeUploadDialog(): void {
    this.displayUploadDialog = false;
    this.uploadPreview = null;
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      this.toastr.warning("Please select an Excel file (.xlsx or .xls)", "Invalid File");
      input.value = "";
      return;
    }

    this.isUploadParsing = true;
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array", cellStyles: true });
        const dateRanges = this.parseVertex42Calendar(workbook);

        if (dateRanges.length === 0) {
          this.toastr.warning(
            "No highlighted time-off dates found in the spreadsheet. Make sure dates are highlighted with a green background.",
            "No Dates Found"
          );
          this.isUploadParsing = false;
          input.value = "";
          return;
        }

        // Extract driver name and year from the spreadsheet
        const calendarSheet = workbook.Sheets[workbook.SheetNames[0]];
        const spreadsheetDriverName = calendarSheet["B7"]?.v?.toString() || null;
        const spreadsheetYear = calendarSheet["D3"]?.v || null;

        // Send to server for preview/validation
        this.driverService
          .previewScheduleUpload({
            driverId: String(this.driver.id),
            dateRanges: dateRanges,
            spreadsheetDriverName: spreadsheetDriverName,
            spreadsheetYear: spreadsheetYear,
          })
          .subscribe({
            next: (preview: any) => {
              this.uploadPreview = preview;
              this.isUploadParsing = false;
            },
            error: (error: any) => {
              console.error("Error previewing schedule upload:", error);
              this.toastr.error("Failed to preview schedule upload", "Error");
              this.isUploadParsing = false;
            },
          });
      } catch (err) {
        console.error("Error parsing Excel file:", err);
        this.toastr.error(
          "Failed to parse the Excel file. Make sure it is a valid Vertex42 calendar template.",
          "Parse Error"
        );
        this.isUploadParsing = false;
      }
      input.value = "";
    };
    reader.readAsArrayBuffer(file);
  }

  private parseVertex42Calendar(workbook: XLSX.WorkBook): ScheduleUploadDateRange[] {
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) return [];

    // The Vertex42 yearly calendar has date cells with datetime values
    // Time-off is marked by green fill (RGB: FF92D050)
    // We scan all cells, find dates with green background, then group into consecutive ranges

    const greenDates: Date[] = [];

    const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
    for (let r = range.s.r; r <= range.e.r; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const cellRef = XLSX.utils.encode_cell({ r, c });
        const cell = sheet[cellRef];
        if (!cell) continue;

        // Check if cell has a date value
        let dateValue: Date | null = null;
        if (cell.t === "d") {
          dateValue = new Date(cell.v);
        } else if (cell.t === "n" && cell.z && /[dmy]/i.test(cell.z)) {
          // Numeric cell with a date format string
          dateValue = this.excelSerialToDate(cell.v);
        }
        if (!dateValue || isNaN(dateValue.getTime())) continue;

        // Check cell style for green fill
        const hasGreenFill = this.cellHasGreenFill(cell, workbook);
        if (hasGreenFill) {
          greenDates.push(dateValue);
        }
      }
    }

    if (greenDates.length === 0) return [];

    // Sort dates and group into consecutive ranges
    greenDates.sort((a, b) => a.getTime() - b.getTime());
    return this.groupConsecutiveDates(greenDates);
  }

  private cellHasGreenFill(cell: any, workbook: XLSX.WorkBook): boolean {
    // SheetJS with cellStyles: true stores style info in cell.s
    if (!cell.s) return false;

    // Check fgColor directly on cell.s (SheetJS pattern)
    if (cell.s.fgColor?.rgb) {
      const normalized = cell.s.fgColor.rgb.toUpperCase().replace(/^FF/, "");
      if (normalized === "92D050") return true;
    }

    // Also check patternFill
    if (cell.s.fill?.fgColor?.rgb) {
      const normalized = cell.s.fill.fgColor.rgb.toUpperCase().replace(/^FF/, "");
      if (normalized === "92D050") return true;
    }

    return false;
  }

  private excelSerialToDate(serial: number): Date {
    const utcDays = Math.floor(serial - 25569);
    const utc = new Date(utcDays * 86400000);
    // Build local-midnight date from UTC components to avoid timezone offset shifting the day
    return new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());
  }

  /**
   * Parses a server date string as a local-midnight Date, ignoring any UTC offset.
   * The server stores schedule dates as date-only values (e.g. "2026-04-15T00:00:00Z").
   * Parsing these directly with `new Date()` shifts them to the previous day in negative-offset
   * timezones (e.g. CDT = UTC-5). Extracting only YYYY-MM-DD prevents that shift.
   */
  private parseLocalDate(value: string | Date | null | undefined): Date {
    if (typeof value === "string") {
      const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
    }
    return new Date(value as any);
  }

  private groupConsecutiveDates(dates: Date[]): ScheduleUploadDateRange[] {
    if (dates.length === 0) return [];

    const ranges: ScheduleUploadDateRange[] = [];
    let start = dates[0];
    let end = dates[0];

    for (let i = 1; i < dates.length; i++) {
      const diff = (dates[i].getTime() - end.getTime()) / 86400000;
      if (Math.abs(diff - 1) < 0.5) {
        end = dates[i];
      } else {
        ranges.push({ start: new Date(start), end: new Date(end) });
        start = dates[i];
        end = dates[i];
      }
    }
    ranges.push({ start: new Date(start), end: new Date(end) });

    return ranges;
  }

  public confirmUpload(): void {
    if (!this.uploadPreview) return;

    this.isUploadConfirming = true;
    this.driverService
      .confirmScheduleUpload({
        driverId: this.uploadPreview.driverId,
        dateRanges: this.uploadPreview.dateRanges,
      })
      .subscribe({
        next: (result: any) => {
          this.lastUploadBatchId = result.batchId;
          this.showUndoBanner = true;

          // Add the new events to the local list
          if (result.createdEvents) {
            result.createdEvents.forEach((event: DriverEventViewModel) => {
              this.timeOffRecords.push(this.mapEventToTimeOffRecord(event));
            });
            this.sortTimeOffRecords();
            this.buildDateColorMap();
          }

          this.toastr.success(
            `${result.eventsCreated} time-off events created from template`,
            "Schedule Uploaded"
          );
          this.isUploadConfirming = false;
          this.closeUploadDialog();
        },
        error: (error: any) => {
          console.error("Error confirming schedule upload:", error);
          this.toastr.error("Failed to upload schedule", "Error");
          this.isUploadConfirming = false;
        },
      });
  }

  public undoUpload(): void {
    if (!this.lastUploadBatchId) return;

    if (!confirm("Undo the last schedule upload? All events from that upload will be removed."))
      return;

    this.driverService.undoScheduleUpload(this.lastUploadBatchId!).subscribe({
      next: (result: any) => {
        this.timeOffRecords = this.timeOffRecords.filter(
          (r) => r.uploadBatchId !== this.lastUploadBatchId
        );
        this.buildDateColorMap();
        this.toastr.success(`${result.eventsRemoved} events removed`, "Upload Undone");
        this.showUndoBanner = false;
        this.lastUploadBatchId = null;
      },
      error: (error: any) => {
        console.error("Error undoing schedule upload:", error);
        this.toastr.error("Failed to undo upload", "Error");
      },
    });
  }

  public dismissUndoBanner(): void {
    this.showUndoBanner = false;
  }

  public clearBatch(record: TimeOffRecord): void {
    const batchId = record.uploadBatchId;
    if (!batchId) return;

    const batchCount = this.timeOffRecords.filter((r) => r.uploadBatchId === batchId).length;

    if (
      !confirm(
        `Remove all ${batchCount} template event${batchCount !== 1 ? "s" : ""} from this upload batch?`
      )
    )
      return;

    this.driverService.undoScheduleUpload(batchId).subscribe({
      next: (result: any) => {
        this.timeOffRecords = this.timeOffRecords.filter((r) => r.uploadBatchId !== batchId);
        this.buildDateColorMap();
        this.toastr.success(`${result.eventsRemoved} template events removed`, "Cleared");
        if (this.lastUploadBatchId === batchId) {
          this.showUndoBanner = false;
          this.lastUploadBatchId = null;
        }
      },
      error: (error: any) => {
        console.error("Error clearing batch:", error);
        this.toastr.error("Failed to clear template upload", "Error");
      },
    });
  }

  public formatDateRange(range: ScheduleUploadDateRange): string {
    const start = this.parseLocalDate(range.start);
    const end = this.parseLocalDate(range.end);
    const days = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
    return `${this.formatDate(start)} - ${this.formatDate(end)} (${days} days)`;
  }

  private static readonly MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  public getPreviewCalendarMonths(): PreviewCalendarMonth[] {
    if (!this.uploadPreview?.dateRanges?.length) return [];

    const year =
      this.uploadPreview.spreadsheetYear ||
      new Date(this.uploadPreview.dateRanges[0].start).getFullYear();

    const months: PreviewCalendarMonth[] = [];

    for (let m = 0; m < 12; m++) {
      const firstDay = new Date(year, m, 1);
      const lastDay = new Date(year, m + 1, 0);
      const weeks: (Date | null)[][] = [];

      // Pad start with nulls for the day-of-week offset (Sunday = 0)
      let week: (Date | null)[] = new Array(firstDay.getDay()).fill(null);

      for (let d = 1; d <= lastDay.getDate(); d++) {
        week.push(new Date(year, m, d));
        if (week.length === 7) {
          weeks.push(week);
          week = [];
        }
      }
      if (week.length > 0) {
        while (week.length < 7) week.push(null);
        weeks.push(week);
      }

      months.push({
        year,
        month: m,
        label: DriverScheduleComponent.MONTH_NAMES[m],
        weeks,
      });
    }

    return months;
  }

  /** Returns a CSS class string for a day cell in the preview calendar. */
  public getPreviewDayClass(date: Date | null): string {
    if (!date || !this.uploadPreview) return "preview-day preview-empty";

    const time = date.getTime();

    // Conflict check (overlap range takes priority for colouring)
    for (const conflict of this.uploadPreview.conflicts) {
      const s = this.parseLocalDate(conflict.overlapStart).setHours(0, 0, 0, 0);
      const e = this.parseLocalDate(conflict.overlapEnd).setHours(23, 59, 59, 999);
      if (time >= s && time <= e) return "preview-day preview-conflict";
    }

    // Time-off range check
    for (const range of this.uploadPreview.dateRanges) {
      const s = this.parseLocalDate(range.start).setHours(0, 0, 0, 0);
      const e = this.parseLocalDate(range.end).setHours(23, 59, 59, 999);
      if (time >= s && time <= e) return "preview-day preview-timeoff";
    }

    return "preview-day";
  }
}
